import { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';

import { useAuth } from '../auth/use-auth.js';
import { AuthLoading } from '../auth/protected-route.js';
import { SecondaryNav } from './secondary-nav.js';
import { ShellProvider } from './shell-context.js';
import { Sidebar } from './sidebar/sidebar.js';
import { Topbar } from './topbar/topbar.js';
import { ToastProvider } from '../toast/toast-context.js';
import { ToastViewport } from '../toast/toast-viewport.js';
import { useSidebarCollapsed } from './use-sidebar-collapsed.js';

export function AppShell(): JSX.Element {
  const { studios, activeStudioId, setActiveStudioId, status } = useAuth();
  const { studioShortId, gameShortId } = useParams<{ studioShortId: string; gameShortId?: string }>();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { collapsed, toggleCollapsed } = useSidebarCollapsed();

  // Close the mobile drawer whenever the route changes so picking a nav item
  // dismisses it without each link wiring its own onClick.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  if (status === 'loading') return <AuthLoading />;

  const studio = studios.find((s) => s.shortId === studioShortId);
  if (studio === undefined) {
    return <Navigate to="/" replace />;
  }

  if (studio.id !== activeStudioId) {
    setActiveStudioId(studio.id);
  }

  return (
    <ToastProvider>
      <ShellProvider studio={studio} gameShortId={gameShortId}>
        <div className="flex h-screen w-screen flex-col overflow-hidden bg-background text-text">
          <Topbar onOpenDrawer={() => setDrawerOpen(true)} />
          <div className="flex min-h-0 flex-1">
            <Sidebar
              drawerOpen={drawerOpen}
              collapsed={collapsed}
              onToggleCollapsed={toggleCollapsed}
              onClose={() => setDrawerOpen(false)}
            />
            {/* The SecondaryNav becomes a horizontal strip on mobile
                (above the outlet) and a left column on md+. */}
            <div className="flex min-w-0 flex-1 flex-col md:flex-row">
              <SecondaryNav />
              <main className="app-grid-bg min-w-0 flex-1 overflow-y-auto">
                <Outlet />
              </main>
            </div>
          </div>
        </div>
      </ShellProvider>
      <ToastViewport />
    </ToastProvider>
  );
}
